import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import Table from "../../components/admin/Table";
import { ActivityLogService } from "../../services/ActivityLogService";
import { DateTime } from "luxon";

const activityLogService = new ActivityLogService();

export default function ActivityLogs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState({
    dateFrom: "",
    dateTo: "",
    category: "",
    action: "",
    status: "",
    search: "",
  });
  const [isFiltering, setIsFiltering] = useState(false);
  const itemsPerPage = 100;

  const fetchLogs = useCallback(
    (page = 1, appliedFilters = {}) => {
      setLoading(true);
      const params = {
        itemsPerPage: itemsPerPage,
        page: page,
      };

      // Add filter parameters if provided
      if (appliedFilters.dateFrom) {
        params.dateFrom = appliedFilters.dateFrom;
      }
      if (appliedFilters.dateTo) {
        params.dateTo = appliedFilters.dateTo;
      }
      if (appliedFilters.category) {
        params.category = appliedFilters.category;
      }
      if (appliedFilters.action) {
        params.action = appliedFilters.action;
      }
      if (appliedFilters.status) {
        params.status = appliedFilters.status;
      }
      if (appliedFilters.search) {
        params.search = appliedFilters.search;
      }

      activityLogService
        .find(params)
        .then((response) => {
          const logsList = response.items.map((item) => ({
            ...item,
            formattedDate: formatDateTime(item.createdAt),
          }));

          setLogs(logsList);
          setTotalItems(response.totalItems || 0);
          setTotalPages(Math.ceil((response.totalItems || 0) / itemsPerPage));
          setCurrentPage(page);

          // Update URL with current page and filters
          const urlParams = { page: page.toString() };
          Object.keys(appliedFilters).forEach((key) => {
            if (appliedFilters[key]) {
              urlParams[key] = appliedFilters[key];
            }
          });
          setSearchParams(urlParams);
        })
        .catch((error) => {
          console.error("Erro ao buscar logs de atividade:", error);
          alert("Não foi possível carregar os logs de atividade.");
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [itemsPerPage, setSearchParams]
  );

  const formatDateTime = (dateString) => {
    try {
      return DateTime.fromISO(dateString, { zone: "UTC" })
        .setZone("America/Sao_Paulo")
        .setLocale("pt-BR")
        .toFormat("dd/MM/yyyy HH:mm:ss");
    } catch (error) {
      console.error("Error formatting datetime:", error);
      return "Data inválida";
    }
  };

  useEffect(() => {
    // Load initial page from URL params or default to page 1
    const pageFromUrl = searchParams.get("page");
    const initialPage = pageFromUrl ? parseInt(pageFromUrl) : 1;

    // Load filters from URL params
    const initialFilters = {
      dateFrom: searchParams.get("dateFrom") || "",
      dateTo: searchParams.get("dateTo") || "",
      category: searchParams.get("category") || "",
      action: searchParams.get("action") || "",
      status: searchParams.get("status") || "",
      search: searchParams.get("search") || "",
    };

    setFilters(initialFilters);
    fetchLogs(initialPage, initialFilters);
  }, []); // Run only on mount

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    setIsFiltering(true);
    fetchLogs(1, filters); // Reset to page 1 when filtering
    setTimeout(() => setIsFiltering(false), 500);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleApplyFilters();
    }
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      dateFrom: "",
      dateTo: "",
      category: "",
      action: "",
      status: "",
      search: "",
    };
    setFilters(clearedFilters);
    fetchLogs(1, clearedFilters);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchLogs(newPage, filters);
      window.scrollTo(0, 0);
    }
  };

  const exportData = async (period, format = "json") => {
    let dateFrom, dateTo;
    let baseFileName;

    if (period === "last30days") {
      const now = DateTime.now().setZone("America/Sao_Paulo");
      dateTo = now.toISODate();
      dateFrom = now.minus({ days: 30 }).toISODate();
      baseFileName = `activity-logs-last-30-days-${now.toFormat("yyyy-MM-dd")}`;
    } else {
      // Period is in format "YYYY-MM"
      const [year, month] = period.split("-");
      const monthStart = DateTime.fromObject(
        { year: parseInt(year), month: parseInt(month), day: 1 },
        { zone: "America/Sao_Paulo" }
      );
      dateFrom = monthStart.toISODate();
      dateTo = monthStart.endOf("month").toISODate();
      baseFileName = `activity-logs-${monthStart.toFormat("MMMM-yyyy", {
        locale: "pt-BR",
      })}`;
    }

    try {
      // Fetch all data for the period (without pagination limit)
      const response = await activityLogService.find({
        dateFrom,
        dateTo,
        itemsPerPage: 10000, // Large number to get all records
      });

      if (format === "csv") {
        // CSV Export
        const csvHeaders = [
          "Data/Hora",
          "Categoria",
          "Ação",
          "Status",
          "Descrição",
        ];
        const csvRows = response.items.map((item) => [
          formatDateTime(item.createdAt),
          item.category || "",
          item.action || "",
          item.status || "",
          (item.description || "").replace(/"/g, '""'), // Escape quotes for CSV
        ]);

        // Create CSV content with BOM for proper UTF-8 encoding in Excel
        const BOM = "\uFEFF";
        const csvContent =
          BOM +
          [
            csvHeaders.map((h) => `"${h}"`).join(","),
            ...csvRows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
          ].join("\n");

        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${baseFileName}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        // JSON Export
        const dataToExport = {
          exportDate: new Date().toISOString(),
          period: period === "last30days" ? "Últimos 30 dias" : period,
          dateRange: {
            from: dateFrom,
            to: dateTo,
          },
          totalItems: response.totalItems || response.items.length,
          data: response.items.map((item) => ({
            ...item,
            formattedDate: formatDateTime(item.createdAt),
          })),
        };

        const blob = new Blob([JSON.stringify(dataToExport, null, 2)], {
          type: "application/json",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${baseFileName}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error(`Erro ao exportar logs como ${format}:`, error);
      alert(`Erro ao exportar logs. Tente novamente.`);
    }
  };

  const getAvailableMonths = () => {
    const months = [];
    const now = DateTime.now().setZone("America/Sao_Paulo");
    const currentMonth = now.startOf("month");

    // Limit to last 6 months (since we're cleaning older logs)
    const sixMonthsAgo = now.minus({ months: 6 }).startOf("month");

    // Start from 6 months ago or January 2024, whichever is more recent
    const systemStartDate = DateTime.fromObject(
      { year: 2024, month: 1, day: 1 },
      { zone: "America/Sao_Paulo" }
    );

    let startDate = sixMonthsAgo > systemStartDate ? sixMonthsAgo : systemStartDate;

    // Generate months from start date to current month
    let date = startDate;
    while (date <= currentMonth) {
      months.push({
        value: date.toFormat("yyyy-MM"),
        label: date
          .toFormat("MMMM yyyy", { locale: "pt-BR" })
          .replace(/^\w/, (c) => c.toUpperCase()),
      });
      date = date.plus({ months: 1 });
    }

    return months.reverse(); // Most recent first
  };

  const columns = [
    { label: "Data/Hora", field: "formattedDate" },
    { label: "Categoria", field: "category" },
    { label: "Ação", field: "action" },
    { label: "Status", field: "status" },
    {
      label: "Descrição",
      field: "description",
      render: (row) => (
        <div dangerouslySetInnerHTML={{ __html: row.description }} />
      )
    },
  ];

  if (loading && currentPage === 1) {
    return (
      <div className="text-center mt-5">Carregando logs de atividade...</div>
    );
  }

  return (
    <div className="container pt-5">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2>Logs de Atividade</h2>
          <small className="text-muted">
            <i className="bi bi-info-circle me-1"></i>
            Os logs são mantidos por até 6 meses
          </small>
        </div>
        <div className="d-flex gap-2">
          <div className="dropdown">
            <button
              className="btn btn-success dropdown-toggle text-nowrap"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="bi bi-download me-1"></i>Exportar JSON
            </button>
            <ul
              className="dropdown-menu dropdown-menu-end"
              style={{ maxHeight: "300px", overflowY: "auto" }}
            >
              <li>
                <button
                  className="dropdown-item"
                  onClick={() => exportData("last30days", "json")}
                >
                  Últimos 30 dias
                </button>
              </li>
              <li>
                <hr className="dropdown-divider" />
              </li>
              {getAvailableMonths().map((month) => (
                <li key={month.value}>
                  <button
                    className="dropdown-item"
                    onClick={() => exportData(month.value, "json")}
                  >
                    {month.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div className="dropdown">
            <button
              className="btn btn-outline-success dropdown-toggle text-nowrap"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="bi bi-file-earmark-spreadsheet me-1"></i>Exportar CSV
            </button>
            <ul
              className="dropdown-menu dropdown-menu-end"
              style={{ maxHeight: "300px", overflowY: "auto" }}
            >
              <li>
                <button
                  className="dropdown-item"
                  onClick={() => exportData("last30days", "csv")}
                >
                  Últimos 30 dias
                </button>
              </li>
              <li>
                <hr className="dropdown-divider" />
              </li>
              {getAvailableMonths().map((month) => (
                <li key={month.value}>
                  <button
                    className="dropdown-item"
                    onClick={() => exportData(month.value, "csv")}
                  >
                    {month.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <div className="card mb-4">
        <div className="card-body">
          {/* Search Field */}
          <div className="row mb-3">
            <div className="col-md-12">
              <label className="form-label fw-bold">
                <i className="bi bi-search me-1"></i>Buscar na Descrição
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Digite para buscar na descrição dos logs..."
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                onKeyDown={handleKeyPress}
              />
            </div>
          </div>

          {/* Filter Fields */}
          <div className="row mb-3">
            <div className="col-md-4">
              <label className="form-label fw-bold">Período</label>
              <div className="row">
                <div className="col-6">
                  <input
                    type="date"
                    className="form-control"
                    value={filters.dateFrom}
                    onChange={(e) =>
                      handleFilterChange("dateFrom", e.target.value)
                    }
                    onKeyDown={handleKeyPress}
                  />
                </div>
                <div className="col-6">
                  <input
                    type="date"
                    className="form-control"
                    value={filters.dateTo}
                    onChange={(e) =>
                      handleFilterChange("dateTo", e.target.value)
                    }
                    onKeyDown={handleKeyPress}
                  />
                </div>
              </div>
            </div>

            <div className="col-md-2">
              <label className="form-label fw-bold">Categoria</label>
              <select
                className="form-select"
                value={filters.category}
                onChange={(e) => handleFilterChange("category", e.target.value)}
              >
                <option value="">Todas</option>
                <option value="AUTH">Autenticação</option>
                <option value="USER_MANAGEMENT">Gestão de Usuários</option>
                <option value="RESERVATION">Reservas</option>
                <option value="SCHOOL_MANAGEMENT">Gestão de Escolas</option>
                <option value="ROLE_MANAGEMENT">Gestão de Roles</option>
                <option value="ADMIN">Admin</option>
                <option value="SYSTEM">Sistema</option>
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label fw-bold">Ação</label>
              <select
                className="form-select"
                value={filters.action}
                onChange={(e) => handleFilterChange("action", e.target.value)}
              >
                <option value="">Todas</option>
                <option value="LOGIN">Login</option>
                <option value="LOGOUT">Logout</option>
                <option value="CREATE">Criar</option>
                <option value="UPDATE">Atualizar</option>
                <option value="DELETE">Deletar</option>
                <option value="VIEW">Visualizar</option>
                <option value="BLOCK">Bloquear</option>
                <option value="UNBLOCK">Desbloquear</option>
                <option value="ENABLE">Ativar</option>
                <option value="DISABLE">Desativar</option>
                <option value="CANCEL">Cancelar</option>
                <option value="CHECKIN">Check-in</option>
                <option value="CHECKOUT">Check-out</option>
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label fw-bold">Status</label>
              <select
                className="form-select"
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <option value="">Todos</option>
                <option value="SUCCESS">Sucesso</option>
                <option value="FAILURE">Falhou</option>
                <option value="WARNING">Aviso</option>
                <option value="INFO">Info</option>
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label fw-bold">&nbsp;</label>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-primary text-nowrap"
                  onClick={handleApplyFilters}
                  disabled={isFiltering}
                >
                  <i className="bi bi-funnel-fill me-1"></i>Filtrar
                </button>
                <button
                  className="btn btn-secondary text-nowrap"
                  onClick={handleClearFilters}
                >
                  <i className="bi bi-eraser-fill me-1"></i>Limpar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <span className="text-muted">
          Total de {totalItems} logs encontrados
        </span>
        <span className="text-muted">
          Página {currentPage} de {totalPages}
        </span>
      </div>

      {/* Table */}
      <div className={isFiltering ? "opacity-50" : ""}>
        <Table
          columns={columns}
          data={logs}
          actions={false} // No actions for activity logs
        />
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <nav aria-label="Navegação de páginas">
          <ul className="pagination justify-content-center mt-4">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Anterior
              </button>
            </li>

            {/* Page numbers */}
            {(() => {
              const pages = [];
              const startPage = Math.max(1, currentPage - 2);
              const endPage = Math.min(totalPages, currentPage + 2);

              if (startPage > 1) {
                pages.push(
                  <li key={1} className="page-item">
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(1)}
                    >
                      1
                    </button>
                  </li>
                );
                if (startPage > 2) {
                  pages.push(
                    <li key="start-ellipsis" className="page-item disabled">
                      <span className="page-link">...</span>
                    </li>
                  );
                }
              }

              for (let i = startPage; i <= endPage; i++) {
                pages.push(
                  <li
                    key={i}
                    className={`page-item ${currentPage === i ? "active" : ""}`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(i)}
                    >
                      {i}
                    </button>
                  </li>
                );
              }

              if (endPage < totalPages) {
                if (endPage < totalPages - 1) {
                  pages.push(
                    <li key="end-ellipsis" className="page-item disabled">
                      <span className="page-link">...</span>
                    </li>
                  );
                }
                pages.push(
                  <li key={totalPages} className="page-item">
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(totalPages)}
                    >
                      {totalPages}
                    </button>
                  </li>
                );
              }

              return pages;
            })()}

            <li
              className={`page-item ${
                currentPage === totalPages ? "disabled" : ""
              }`}
            >
              <button
                className="page-link"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Próxima
              </button>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
}
